'use strict';
namespace TR {

    import IScope = angular.IScope;

    interface RouteParams extends ng.route.IRouteService {
        tripId: string;
    }

    interface RequestedItemCost {
        CurrencyCode :string;
        Total :number;
    }

    class CarHireItem {
        carHireCode :string;
        quote :Core.CarHire.Quote;
        details :any;
        selectedOptions :string[] = [];

        constructor(code :string) {
            this.carHireCode = code;
        }

        public static createItemFromQuote(quote :Core.CarHire.Quote) {
            let item = new CarHireItem(quote.CarHireId.CarHireCode);
            item.quote = quote;
            return item;
        }
    }

    enum ViewMode {
        Results = 1,
        Options = 2,
        Terms = 3
    }

    class CrossSellCarHire {

        private basketId :string;
        private optionsPromise :ng.IPromise<any> = this.$q.when();
        private searchId :string|null;
        private readonly tripId :string;

        public driverAge :number = 30;
        public driverAges :number[] = [];
        public dropOffDate :string;
        public items :CarHireItem[] = [];
        public fromPrice :number = 0;
        public readonly noneSelection :CarHireItem = new CarHireItem('-1');
        public pickUpDate :string;
        public selectedItem :CarHireItem;
        public showError :boolean;
        public totalCost :number;
        public payNowCost :number;
        public payLocallyCosts :RequestedItemCost[] = [];
        public carInsuranceInfo :any;
        public duration: number;

        //properties used to control the UI state
        public isBusy :boolean = true;
        public showResults :boolean;
        public showInsurance :boolean;
        public showExplained :boolean;
        public readonly viewMode = ViewMode; //so we can use it in the template
        public view :ViewMode = ViewMode.Results;
        public carsAvailabilityBusy :boolean = true;

        constructor(private $q :ng.IQService,
                    private $routeParams :RouteParams,
                    private $scope: IScope,
                    private analytics :any,
                    private arrayUtil :ArrayUtil,
                    private basketService :any,
                    private basketStore :any,
                    private dateUtil: any,
                    private tripService :Core.TripService) {

            this.driverAges = arrayUtil.createRange(90, 21);
            this.selectedItem = this.noneSelection;
            this.tripId = $routeParams.tripId;

            tripService.getTrip(this.tripId)
                .then((trip :Core.Trip.TripState) => {

                    this.basketId = <string>trip.BasketId;

                    if (trip.CarHireRequest) {
                        this.setLocalPropertiesFromRequest(trip.CarHireRequest);
                        this.searchId = <string>trip.CarHireRequest.AvailabilitySearchId;
                    }

                    $q.all([
                        basketStore.carHire(this.basketId),
                        basketStore.carHireOptions(this.basketId, $routeParams),
                        this.retrieveQuotes(this.searchId, this.basketId, this.tripId, this.driverAge)
                    ]).then((results) => {

                        let selected = results[0];
                        this.setResults(results[2]); //set this.items

                        //find the car hire item from our quotes so that we can select it
                        if (selected) {
                            const item = this.findCarHireItem(this.items, selected.Details.CarHireId.CarHireCode);
                            item.details = selected.Details;
                            item.selectedOptions = results[1];
                            this.selectedItem = item;
                            this.updateTotalCost();
                        }

                        if(!this.searchId) {
                            tripService.getTrip(this.tripId)
                                .then(trip => {
                                    this.setLocalPropertiesFromRequest(trip.CarHireRequest);
                                    this.searchId = trip.CarHireRequest.AvailabilitySearchId;
                                });
                        }

                        // Watch the additional options collection and update the basket when this changes
                        $scope.$watchCollection ('$ctrl.selectedItem.selectedOptions', (val, old) => {
                            if (this.selectedItem.carHireCode === '-1') {
                                // no car selected
                                return;
                            }
                            if (val !== old) {
                                this.updateOptions();
                            }
                        });


                    }).finally(() => {
                        this.isBusy = false;
                        this.carsAvailabilityBusy = false;
                    });

            }, this.basketService.bookingAlreadyDone(`crosssellCarHire: '${this.tripId}'`));
        }

        private retrieveQuotes(searchId :string | null,
                               basketId :string,
                               tripId :string,
                               age :number
                              ) :ng.IPromise<CarHireItem[]> {

            const mapQuotes = (quotes :Core.CarHire.Quote[]) => {
                return _.map(
                    _.sortBy(quotes, 'Price'),
                    (q :Core.CarHire.Quote) => CarHireItem.createItemFromQuote(q)
                );
            };

            if (searchId != null) {
                return this.basketService.getCarQuotesFromSession(searchId, 0)
                    .then((quotes :Core.CarHire.Quote[]) => mapQuotes(quotes));
            } else {
                return this.basketService.getCarQuotes(basketId, tripId, age)
                    .then((quotes :Core.CarHire.Quote[]) => mapQuotes(quotes));
            }
        }

        private findCarHireItem(list :CarHireItem[], carCode :string) :CarHireItem {
            return _.find(list, {'carHireCode': carCode});
        }

        private setResults(list :CarHireItem[]) :void {
            this.items = list;
            this.carsAvailabilityBusy = false;

            //this should really be done service side not a problem for now as long as we do not show more than
            // one page worth of results
            this.fromPrice = this.items.length ? _.minBy(this.items, 'quote.Price').quote.Price : 0;

        }

        private setLocalPropertiesFromRequest(request :Core.Trip.CarHireRequest) {
            this.driverAge = request.DriverAge;
            this.dropOffDate = request.DropOffDate;
            this.pickUpDate = request.PickUpDate;
        }

        //CHECKOUT-47 : extra param just to do some mixpanel tracking
        //will be removed once the test is finished
        public showModal(show, logMixPanelEvent) :void {
            this.showResults = show;

            if (show === false) {
                this.view = ViewMode.Results;
                this.showError = false;
            }

            if (logMixPanelEvent === true) {
                this.analytics.track('mixpanel', 'OptionsCarHireChooseClick');
            }
        }

        public previousView($event) :void {
            $event.preventDefault();
            $event.stopPropagation();
            this.view -= 1;
        }

        public displayTerms($event) :void {
            $event.preventDefault();
            $event.stopPropagation();
            this.view = ViewMode.Terms;
        }

        public selectCar(carHireCode) :void {

            this.isBusy = true;
            this.showError = false;

            if (this.selectedItem.carHireCode === carHireCode) {
                //we already have it selected move the user along to the next page
                this.view = ViewMode.Options;
                this.isBusy = false;
                this.getCarInsuranceInfo(this.selectedItem.details.AdditionalOptions);
                this.duration = this.getDuration(this.selectedItem.details.HireDetails);
            } else {

                //find the quote from our list and select it
                const item = this.findCarHireItem(this.items, carHireCode);
                item.selectedOptions = []; //clear it out, may have been previously selected

                this.basketService.selectCar(item.quote, this.searchId, this.basketId)
                    .then(carDetails => {
                        item.details = carDetails.Details;
                        this.selectedItem = item;
                        this.isBusy = false;
                        this.view = ViewMode.Options;
                        this.getCarInsuranceInfo(this.selectedItem.details.AdditionalOptions);
                        this.duration = this.getDuration(this.selectedItem.details.HireDetails);
                    })
                    .then(() => this.updateTotalCost())
                    .catch(() => {
                        this.selectedItem = this.noneSelection;
                        this.showError = true;
                        this.isBusy = false;
                    });
            }
        }

        public removeCar() :ng.IPromise<void> {
            return this.basketStore.removeCar(this.basketId)
                .then(() => {
                    this.selectedItem = this.noneSelection;
                    this.view = ViewMode.Results;
                })
                .then(() => this.basketService.updateBasket());
        }

        private updateOptions() {
            this.optionsPromise = this.optionsPromise
                                    .then(() => this.updateBasketOptions(
                                                    this.selectedItem.selectedOptions,
                                                    this.selectedItem.details.DetailsSearchId
                                                )
                                    ).then(
                                        () => this.updateTotalCost()
                                    );
        }

        private updateBasketOptions(options :string[], detailsSearchId: string) {

            return this.basketStore.carHireOptions(this.basketId).then( basketOptions => {

                const _createBasketOption = (id: string, quantity :number) => {
                    return { OptionId : id, ChosenQuantity : quantity};
                };

                const optionsToRemove = _.map(this.arrayUtil.difference(basketOptions, options),
                                              o => _createBasketOption(o,1) );
                const optionsToAdd = _.map(this.arrayUtil.difference(options, basketOptions),
                                           o => _createBasketOption(o, 1) );

                return this.basketStore.removeOptions(this.basketStore.productType.CarHire,
                                                        detailsSearchId,
                                                        optionsToRemove,
                                                        this.basketId)
                    .then(() => this.basketStore.addOptions(this.basketStore.productType.CarHire,
                                                            detailsSearchId,
                                                            optionsToAdd,
                                                            this.basketId))
                    .then(() => this.basketService.updateBasket());
            });
        }

        private updateTotalCost() :void {
            this.$q.all([
                        this.basketStore.carHireCost(this.basketId),
                        this.basketStore.carHireRequestedItemsCosts(this.basketId)
                       ])
                .then(results => {
                    this.payNowCost = results[0];
                    this.payLocallyCosts = results[1];
                    //this is ok currently as car hire extras are in the same currency as the original request
                    this.totalCost = _.reduce(this.payLocallyCosts, (s,n)=> { return s + n.Total; }, this.payNowCost);
                });
        }

        public driverAgeChanged() :void {

            this.isBusy = true;
            this.searchId = null;

            //remove any existing selection and get the new list and new search id
            this.$q.all([
                this.removeCar(),
                this.retrieveQuotes(this.searchId, this.basketId, this.tripId, this.driverAge)
            ]).then(results => {

                this.setResults(results[1]); //set this.items

                this.tripService.getTrip(this.tripId)
                    .then(trip => {
                        this.searchId = trip.CarHireRequest.AvailabilitySearchId;
                        this.isBusy = false;
                    });
            });
        }

        public getCarInsuranceInfo(options) :void {
            // get insurance information data from option where it`s declared
            if(options) {
                options = options.filter((item) => item.Price.PayableLocally === false);
                this.carInsuranceInfo = options[0].InsuranceInformation;
            };
        }

        public showInsuranceExplained(show) :void {
            // open insurance explained modal and close insurance info modal
            this.showExplained = show;
            this.removeFlashing();
            if (!show) {
                this.showModal(true, true);
                this.view = ViewMode.Options;
            } else {
                const docName = '/build/docs/BookingConditions/IPID-DocumentCarTrawlerDamageRefund.pdf';
                this.showModal(false, null);
                this.carInsuranceInfo.DemageRefundDoc = docName;
            }
        }

        public viewTerms() :void {
            // close insurance explained modal and go to open car hire terms modal
            this.showExplained = false;
            this.removeFlashing();
            this.showModal(true, true);
            this.view = ViewMode.Terms;
        }

        public removeFlashing() :void {
            // hack to remove flashing of background when switching to another popup
            $('.modal-overlay').stop().fadeOut();
        }

        public getDuration(hireDetails) {
            let parsedDateFrom =  this.dateUtil.parseDate(hireDetails.PickUpTime);
            let parsedDateTo =  this.dateUtil.parseDate(hireDetails.DropOffTime);
            return this.dateUtil.getDifferenceInDays(parsedDateFrom, parsedDateTo);
        }
    }

    angular
        .module('TR')
        .component('crossSellCarHire', {
            bindings: {},
            controller: CrossSellCarHire,
            templateUrl: 'partials/home/directivetemplate?type=CrossSellCarHire'
        });
}
